import React from "react";
import {
    Badge,
    Box,
    Stack,
    Text,
    TextField,
} from "braid-design-system";
import { BomMenuItem } from "../../types/bom";
import ItemThumbnail from "./ItemThumbnail";

interface BomMenuItemListProps {
    items: BomMenuItem[];
    selectedItemCode: string | null;
    searchTerm: string;
    onSearchChange: (value: string) => void;
    onSelectItem: (itemCode: string) => void;
}

const BomMenuItemList: React.FC<BomMenuItemListProps> = ({
    items,
    selectedItemCode,
    searchTerm,
    onSearchChange,
    onSelectItem,
}) => {
    const filteredItems = React.useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) return items;
        return items.filter(
            (item) =>
                item.item_code.toLowerCase().includes(term) ||
                item.item_name.toLowerCase().includes(term),
        );
    }, [items, searchTerm]);

    return (
        <Box background="surface" borderRadius="standard" boxShadow="borderNeutralLight" padding="small">
            <Stack space="small">
                <Text weight="strong" size="small" tone="secondary">
                    Menu Item
                </Text>

                <TextField
                    placeholder="Search item by code or name..."
                    aria-label="Search menu items"
                    value={searchTerm}
                    onChange={(e) => onSearchChange(e.currentTarget.value)}
                />

                <Box style={{ maxHeight: 560, overflowY: "auto" }}>
                    <Stack space="xxsmall">
                        {filteredItems.length === 0 ? (
                            <Box padding="medium">
                                <Text tone="secondary" align="center">
                                    No items found.
                                </Text>
                            </Box>
                        ) : (
                            filteredItems.map((item) => {
                                const selected = item.item_code === selectedItemCode;
                                return (
                                    <Box
                                        key={item.item_code}
                                        paddingX="small"
                                        paddingY="small"
                                        borderRadius="standard"
                                        background={selected ? "brandAccentSoft" : undefined}
                                        cursor="pointer"
                                        display="flex"
                                        alignItems="center"
                                        gap="small"
                                        onClick={() => onSelectItem(item.item_code)}
                                    >
                                        <ItemThumbnail imageUrl={item.image_url} alt={item.item_name} size={40} fontSize={20} />
                                        <Box flexGrow={1} style={{ minWidth: 0 }}>
                                            <Stack space="xxsmall">
                                                <Text
                                                    size="small"
                                                    weight="strong"
                                                    tone={selected ? "brandAccent" : undefined}
                                                >
                                                    {item.item_code}
                                                </Text>
                                                <Text size="small" tone="secondary">
                                                    {item.item_name}
                                                </Text>
                                            </Stack>
                                        </Box>
                                        {item.ingredients.length === 0 ? (
                                            <Badge tone="neutral" weight="regular">
                                                No BOM
                                            </Badge>
                                        ) : null}
                                    </Box>
                                );
                            })
                        )}
                    </Stack>
                </Box>
            </Stack>
        </Box>
    );
};

export default BomMenuItemList;
